/**
 * Action-first mobile delivery card (Sprint 8.5) — mock-aligned layout.
 * Compact by default: labeled stepper expands under "Journey".
 */

import React, { useMemo, useState } from "react";
import { Button, Drawer } from "antd";
import {
  CalendarOutlined,
  CheckOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  LinkOutlined,
  MessageOutlined,
  MoreOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { getMilestoneView } from "../services/orderMilestoneView.js";
import TripLabelChip from "./TripLabelChip.jsx";
import { getAssignedOwner, getStageProgress } from "../services/orderMissionSummary.js";
import {
  computeOrderHealth,
  DEFAULT_HEALTH_THRESHOLDS,
} from "../services/orderOperationalStatus.js";
import { getOrderTrackingLink } from "../services/orderService.js";

const STATUS_VISUAL = {
  pending: { label: "Pending assignment", color: "#fa8c16", bg: "#fff7e6" },
  booked: { label: "Booked", color: "#1890ff", bg: "#e6f7ff" },
  in_progress: { label: "Collected / in progress", color: "#1890ff", bg: "#e6f7ff" },
  arrived_at_storage: { label: "At storage", color: "#722ed1", bg: "#f9f0ff" },
  in_transit_from_storage: { label: "In transit", color: "#13c2c2", bg: "#e6fffb" },
  completed: { label: "Delivered", color: "#52c41a", bg: "#f6ffed" },
  cancelled: { label: "Cancelled", color: "#ff4d4f", bg: "#fff1f0" },
};

const HEALTH_LABEL = {
  onTrack: { text: "On track", color: "#52c41a" },
  attention: { text: "Needs attention", color: "#faad14" },
  delayed: { text: "Delayed", color: "#ff4d4f" },
  blocked: { text: "Blocked", color: "#ff4d4f" },
};

const Card = styled.article`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const Body = styled.div`
  padding: 14px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${(props) => props.$color};
  background: ${(props) => props.$bg};
`;

const PriorityLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ca3af;
`;

const MoreBtn = styled.button`
  margin-left: auto;
  border: none;
  background: transparent;
  color: #6b7280;
  padding: 4px 6px;
  border-radius: 8px;
  cursor: pointer;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrderId = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
`;

const Customer = styled.div`
  font-size: 14px;
  color: #4b5563;
  margin-top: 2px;
`;

const RouteLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
  flex-wrap: wrap;
`;

const JourneyRing = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: conic-gradient(
    ${(props) => props.$color} ${(props) => props.$percent}%,
    #e5e7eb 0
  );
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 5px;
    border-radius: 50%;
    background: #fff;
  }
`;

const RingInner = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  line-height: 1.05;
`;

const RingPct = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: ${(props) => props.$color};
`;

const RingLabel = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin: 14px 0 12px;
  padding: 10px 0;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
`;

const MetaCell = styled.div`
  min-width: 0;
`;

const MetaLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
`;

const MetaValue = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${(props) => props.$color || "#111827"};
  margin-top: 3px;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const HealthDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(props) => props.$color};
  margin-right: 4px;
  vertical-align: middle;
`;

const Stepper = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 14px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
`;

const Step = styled.div`
  flex: 1 0 56px;
  min-width: 56px;
  text-align: center;
  position: relative;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    top: 9px;
    left: calc(50% + 10px);
    right: calc(-50% + 10px);
    height: 2px;
    background: ${(props) =>
      props.$state === "completed" || props.$state === "current"
        ? props.$accent
        : "#e5e7eb"};
  }
`;

const StepDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 10px;
  position: relative;
  z-index: 1;
  background: ${(props) => {
    if (props.$state === "completed") return props.$accent;
    if (props.$state === "current") return "#fff";
    return "#f3f4f6";
  }};
  border: 2px solid
    ${(props) => {
      if (props.$state === "completed" || props.$state === "current") {
        return props.$accent;
      }
      return "#e5e7eb";
    }};
  color: ${(props) =>
    props.$state === "completed" ? "#fff" : props.$accent};
`;

const StepTitle = styled.div`
  margin-top: 6px;
  font-size: 10px;
  font-weight: ${(props) => (props.$current ? 700 : 500)};
  color: ${(props) => (props.$current ? props.$accent : "#9ca3af")};
  line-height: 1.2;
`;

const StepSub = styled.div`
  font-size: 9px;
  color: #9ca3af;
  margin-top: 2px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
`;

const QuickBtn = styled(Button)`
  height: 42px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  padding-inline: 10px;
`;

const PrimaryButton = styled(Button)`
  flex: 1;
  height: 42px;
  font-weight: 700;
  border-radius: 10px;
`;

const COMPACT_STAGE_LABELS = {
  booking_confirmed: "Booked",
  pickup_scheduled: "Scheduled",
  load_collected: "Collected",
  at_storage: "Storage",
  trip_scheduled: "Trip",
  loaded: "Loaded",
  in_transit: "In transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

function shortPlace(value) {
  if (!value) return "—";
  const text = typeof value === "string" ? value : value.address || String(value);
  const first = text.split(",")[0]?.trim();
  return first || text;
}

function shortName(name) {
  if (!name) return "Customer";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function formatPhoneForWhatsApp(phone) {
  if (!phone) return null;
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.startsWith("27")) return digitsOnly;
  if (digitsOnly.startsWith("0")) return `27${digitsOnly.substring(1)}`;
  if (digitsOnly.length >= 9) return `27${digitsOnly}`;
  return digitsOnly;
}

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `R ${num.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Best-effort outstanding from list payloads (numeric or financialStatus). */
function resolveOutstanding(order) {
  const candidates = [
    order?.invoicesRollup?.balance,
    order?.paymentSummary?.balance,
    order?.financial?.balance,
    order?.outstandingBalance,
  ];
  for (const value of candidates) {
    const num = Number(value);
    if (Number.isFinite(num)) {
      return { kind: "amount", value: num };
    }
  }
  const fs = order?.financialStatus;
  if (fs === "Paid" || fs === "Refunded") {
    return { kind: "status", value: 0, label: "Clear" };
  }
  if (fs === "PartiallyPaid") {
    return { kind: "status", value: 1, label: "Partial" };
  }
  if (fs === "Pending") {
    return { kind: "status", value: 1, label: "Unpaid" };
  }
  return null;
}

function getPrimaryAction(order) {
  switch (order?.status) {
    case "pending":
    case "booked":
      return { key: "assign", label: "Assign Driver" };
    case "in_progress":
      return { key: "update", label: "Update Status" };
    case "arrived_at_storage":
      return { key: "update", label: "Schedule Delivery" };
    case "in_transit_from_storage":
      return { key: "update", label: "Mark Delivered" };
    case "completed":
      return { key: "view", label: "View Details" };
    case "cancelled":
      return { key: "archive", label: "Archive" };
    default:
      return { key: "update", label: "Update Status" };
  }
}

function getHealthDisplay(health, owner) {
  if (!health?.applicable) {
    return { text: "—", color: "#9ca3af" };
  }
  if (health.health === "onTrack") {
    if (!owner?.partnerName && !owner?.driverName) {
      return { text: "Driver not assigned", color: "#faad14" };
    }
    return HEALTH_LABEL.onTrack;
  }
  if (health.reason) {
    return {
      text: health.reason,
      color: (HEALTH_LABEL[health.health] || HEALTH_LABEL.attention).color,
    };
  }
  return HEALTH_LABEL[health.health] || HEALTH_LABEL.attention;
}

export default function MobileDeliveryCard({
  order,
  trip = null,
  thresholds = DEFAULT_HEALTH_THRESHOLDS,
  formatDateDisplay,
  getExpectedDeliveryDate,
  onPrimaryAction,
  onOpenMap,
  onViewDetails,
  sheetItems = [],
}) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const visual = STATUS_VISUAL[order.status] || STATUS_VISUAL.pending;
  const milestone = useMemo(() => getMilestoneView(order), [order]);
  const progress = useMemo(() => getStageProgress(order), [order]);
  const owner = useMemo(() => getAssignedOwner(order), [order]);
  const health = useMemo(() => {
    if (order?.loadHealth && typeof order.loadHealth === "object") {
      return order.loadHealth;
    }
    return computeOrderHealth(order, thresholds);
  }, [order, thresholds]);
  const primary = getPrimaryAction(order);
  const healthDisplay = getHealthDisplay(health, owner);
  const eta = getExpectedDeliveryDate?.(order);
  const outstanding = resolveOutstanding(order);
  const outstandingLabel =
    outstanding == null
      ? "—"
      : outstanding.kind === "amount"
        ? outstanding.value > 0.01
          ? formatMoney(outstanding.value)
          : "Clear"
        : outstanding.label;
  const outstandingWarn =
    outstanding != null &&
    ((outstanding.kind === "amount" && outstanding.value > 0.01) ||
      (outstanding.kind === "status" && outstanding.value > 0));
  const [trackerLoading, setTrackerLoading] = useState(false);
  const wa = formatPhoneForWhatsApp(order.customerPhone);
  const percent = progress.percent ?? 0;
  const ringColor =
    health.health === "delayed" || health.health === "blocked"
      ? "#ff4d4f"
      : health.health === "attention"
        ? "#faad14"
        : visual.color;
  const stages = milestone.stages.filter((s) => s.key !== "cancelled").slice(0, 5);
  const pickup = shortPlace(order.pickup?.address || order.pickup);
  const dropoff = shortPlace(order.delivery?.address || order.delivery);

  const handleTracker = async (e) => {
    e.stopPropagation();
    const orderRef = order.id || order.jobId;
    if (!orderRef || trackerLoading) return;
    setTrackerLoading(true);
    try {
      const { trackingUrl } = await getOrderTrackingLink(orderRef);
      if (trackingUrl) {
        window.open(trackingUrl, "_blank", "noopener,noreferrer");
      }
    } catch {
      // Tracker unavailable — no-op on list card
    } finally {
      setTrackerLoading(false);
    }
  };

  return (
    <Card>
      <Body>
        <HeaderRow>
          <StatusBadge $color={visual.color} $bg={visual.bg}>
            {visual.label}
          </StatusBadge>
          <PriorityLabel>
            {(order.priority || "normal").toString()}
          </PriorityLabel>
          <MoreBtn
            type="button"
            aria-label="More actions"
            onClick={(e) => {
              e.stopPropagation();
              setSheetOpen(true);
            }}
          >
            <MoreOutlined />
          </MoreBtn>
        </HeaderRow>

        <TitleRow>
          <TitleBlock>
            <OrderId>{order.id}</OrderId>
            <Customer>{shortName(order.customer)}</Customer>
            <RouteLine>
              <EnvironmentOutlined style={{ color: "#1890ff" }} />
              <span>{pickup}</span>
              <span aria-hidden="true">→</span>
              <EnvironmentOutlined style={{ color: "#52c41a" }} />
              <span>{dropoff}</span>
            </RouteLine>
            <div
              style={{ marginTop: 8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <TripLabelChip tripId={order.tripId} trip={trip} />
            </div>
          </TitleBlock>
          <JourneyRing
            $percent={percent}
            $color={ringColor}
            title={`${progress.label}: ${percent}%`}
            aria-label={`${percent}% journey`}
          >
            <RingInner>
              <RingPct $color={ringColor}>{percent}%</RingPct>
              <RingLabel>Journey</RingLabel>
            </RingInner>
          </JourneyRing>
        </TitleRow>

        <MetaGrid>
          <MetaCell>
            <MetaLabel>
              <CalendarOutlined /> ETA
            </MetaLabel>
            <MetaValue>
              {eta && formatDateDisplay ? formatDateDisplay(eta) : "—"}
            </MetaValue>
          </MetaCell>
          <MetaCell>
            <MetaLabel>
              <DollarOutlined /> Outstanding
            </MetaLabel>
            <MetaValue $color={outstandingWarn ? "#d48806" : undefined}>
              {outstandingLabel}
            </MetaValue>
          </MetaCell>
          <MetaCell>
            <MetaLabel>Health</MetaLabel>
            <MetaValue $color={healthDisplay.color}>
              <HealthDot $color={healthDisplay.color} />
              {healthDisplay.text}
            </MetaValue>
          </MetaCell>
        </MetaGrid>

        <Stepper aria-label="Journey milestones">
          {stages.map((step) => (
            <Step
              key={step.key}
              $state={step.state}
              $accent={ringColor}
            >
              <StepDot $state={step.state} $accent={ringColor}>
                {step.state === "completed" ? <CheckOutlined /> : null}
              </StepDot>
              <StepTitle
                $current={step.state === "current"}
                $accent={ringColor}
              >
                {COMPACT_STAGE_LABELS[step.key] || step.title}
              </StepTitle>
              {step.state === "current" ? (
                <StepSub>Current</StepSub>
              ) : null}
            </Step>
          ))}
        </Stepper>

        <ActionsRow onClick={(e) => e.stopPropagation()}>
          <QuickBtn
            icon={<PhoneOutlined />}
            href={order.customerPhone ? `tel:${order.customerPhone}` : undefined}
            disabled={!order.customerPhone}
          />
          <QuickBtn
            icon={<MessageOutlined />}
            href={wa ? `https://wa.me/${wa}` : undefined}
            target="_blank"
            disabled={!wa}
            style={wa ? { color: "#25d366", borderColor: "#25d366" } : undefined}
          />
          <QuickBtn icon={<LinkOutlined />} onClick={handleTracker} />
          <PrimaryButton
            type="primary"
            onClick={() => {
              if (primary.key === "view") {
                onViewDetails?.(order);
              } else {
                onPrimaryAction?.(primary.key, order);
              }
            }}
          >
            {primary.label}
          </PrimaryButton>
        </ActionsRow>
      </Body>

      <Drawer
        title={order.id}
        placement="bottom"
        height="auto"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        styles={{ body: { paddingTop: 8, paddingBottom: 24 } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Button
            block
            icon={<EnvironmentOutlined />}
            onClick={() => {
              setSheetOpen(false);
              onOpenMap?.(order);
            }}
            style={{ height: 44, justifyContent: "flex-start" }}
          >
            View map
          </Button>
          <Button
            block
            onClick={() => {
              setSheetOpen(false);
              onViewDetails?.(order);
            }}
            style={{ height: 44, justifyContent: "flex-start" }}
          >
            Full order details
          </Button>
          {sheetItems.map((item) =>
            item.type === "divider" ? null : (
              <Button
                key={item.key}
                block
                danger={item.danger}
                icon={item.icon}
                onClick={() => {
                  setSheetOpen(false);
                  item.onClick?.();
                }}
                style={{ height: 44, justifyContent: "flex-start" }}
              >
                {item.label}
              </Button>
            )
          )}
        </div>
      </Drawer>
    </Card>
  );
}
